import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldAlert, Flame, MessageSquareReply, TrendingUp, TrendingDown, Minus, Bell, Heart, Anchor, HeartHandshake, Sparkles, Trash2, RefreshCw, Loader2, MessageCircle, Brain, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api, vibrate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const QUIZ_FR = { secure: "sécure", anxious: "anxieux", avoidant: "évitant", disorganized: "désorganisé" };
const STYLE_EMO = { "sécure": "🌿", "anxieux": "😰", "évitant": "🥶", "désorganisé": "🎢" };
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const normStyle = (s) => {
  const x = (s || "").toLowerCase().trim();
  if (x.startsWith("anx")) return "anxieux";
  if (x.startsWith("évit") || x.startsWith("evit")) return "évitant";
  if (x.startsWith("séc") || x.startsWith("sec")) return "sécure";
  if (x.startsWith("dés") || x.startsWith("des")) return "désorganisé";
  return null;
};
const TRAP = {
  "anxieux-évitant": "Le piège classique 🎢 : plus tu cherches à te rapprocher, plus l'autre prend ses distances. Vous alimentez le manque l'un de l'autre.",
  "anxieux-anxieux": "Deux cœurs à vif : beaucoup d'intensité… mais aussi de montagnes russes émotionnelles.",
  "anxieux-sécure": "Bonne nouvelle 🌿 : sa stabilité peut vraiment t'apaiser. Laisse-toi rassurer.",
  "anxieux-désorganisé": "Terrain instable : ses signaux contradictoires risquent de nourrir ton anxiété. Vigilance.",
  "évitant-anxieux": "Attention : son besoin de proximité peut te donner envie de fuir. Nomme ton besoin d'espace au lieu de disparaître.",
  "évitant-évitant": "Deux indépendants : confortable en surface, mais l'intimité risque de rester à distance.",
  "évitant-sécure": "Sa sécurité 🌿 peut t'aider à t'ouvrir sans te sentir envahi·e.",
  "évitant-désorganisé": "Combo compliqué : ses hauts et bas + ton besoin de distance = incompréhensions.",
  "sécure-anxieux": "Ta stabilité rassure : tu peux l'aider à s'apaiser (sans porter son anxiété à sa place).",
  "sécure-évitant": "Ta sécurité peut inviter l'autre à baisser la garde. Patience et clarté.",
  "sécure-sécure": "Base saine 🌿 : communication et confiance au rendez-vous.",
  "sécure-désorganisé": "Ta stabilité est précieuse ici — protège tes limites face aux montagnes russes.",
  "désorganisé-anxieux": "Vos deux sensibilités s'entremêlent : repère tes déclencheurs pour éviter le cycle attirance/rejet.",
  "désorganisé-évitant": "Tes hauts et bas face à sa distance : dynamique instable, avance en conscience.",
  "désorganisé-sécure": "Sa stabilité peut t'ancrer — un vrai point d'appui pour sortir des montagnes russes.",
  "désorganisé-désorganisé": "Deux montagnes russes ensemble : intense mais épuisant. Un cadre clair vous aiderait.",
};
const trapText = (me, them) => TRAP[`${me}-${them}`] || `Ton style (${me}) rencontre un profil ${them} : découvre votre dynamique dans le Verdict complet.`;

const VLABEL = {
  red_flags: { label: "Red Flags", icon: ShieldAlert },
  rizz_score: { label: "Rizz Score", icon: Flame },
  reply_suggestions: { label: "Réponses", icon: MessageSquareReply },
  attachment_style: { label: "Attachement", icon: Anchor },
  compatibility: { label: "Compatibilité", icon: HeartHandshake },
};
const TREND = {
  up: { icon: TrendingUp, c: "text-emerald-400", t: "En amélioration" },
  down: { icon: TrendingDown, c: "text-red-400", t: "En dégradation" },
  flat: { icon: Minus, c: "text-zinc-400", t: "Stable" },
};
// Deep axes (attachment + compatibility) unlock by message volume, NOT by plan.
const DEEP_MIN = 12;   // minimum to generate a preview
const DEEP_FULL = 25;  // full-reliability threshold

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return ""; }
}
function healthColor(s) {
  if (s == null) return "#71717a";
  return s < 40 ? "#ef4444" : s < 70 ? "#f59e0b" : "#10b981";
}

export default function RelationshipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    return api.get(`/relationships/${id}`).then((r) => {
      setData(r.data);
      (r.data.alerts || []).filter((a) => !a.read).forEach((a) => api.post(`/alerts/${a.alert_id}/read`).catch(() => {}));
    }).catch(() => navigate("/app"));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    vibrate(); setSyncing(true);
    try {
      const { data: res } = await api.post(`/whatsapp/sync/${id}`);
      if (res.analyzed) toast.success("Historique synchronisé et analysé");
      else toast.message(res.info || "Synchronisation effectuée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synchronisation impossible");
    } finally { setSyncing(false); }
  };

  const deleteRel = async () => {
    if (!window.confirm("Supprimer définitivement cette relation et tout son historique ?")) return;
    vibrate(); setDeleting(true);
    try {
      await api.delete(`/relationships/${id}`);
      toast.success("Relation supprimée");
      navigate("/app");
    } catch (e) {
      toast.error("Suppression impossible");
      setDeleting(false);
    }
  };

  if (!data) {
    return <Layout><div className="flex justify-center py-12"><div className="w-8 h-8 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" /></div></Layout>;
  }

  const rel = data.relationship;
  const hist = rel.health_score_history || [];
  const tr = TREND[rel.trend] || TREND.flat;
  const TrIcon = tr.icon;
  const hc = healthColor(rel.health_score);
  const isWhatsapp = rel.source === "whatsapp";
  const msgCount = rel.message_count || 0;

  return (
    <Layout>
      <button data-testid="rel-back" onClick={() => navigate("/app")} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4">
        <ArrowLeft size={16} /> Mes relations
      </button>

      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500/30 to-purple-500/30 flex items-center justify-center text-pink-300 font-display font-black text-lg">
          {(rel.contact_name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h1 data-testid="rel-name" className="font-display text-2xl font-black tracking-tight leading-none truncate">{rel.contact_name}</h1>
          <p className="text-xs text-zinc-500 mt-1 capitalize">{rel.relationship_type} · suivi depuis {fmtDate(rel.started_tracking_at)}</p>
        </div>
        <button data-testid="rel-delete" onClick={deleteRel} disabled={deleting}
          title="Supprimer la relation"
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50">
          {deleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-2xl glass-card p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Health score</p>
          <p className="font-display text-4xl font-black mt-1" style={{ color: hc }}>{rel.health_score ?? "—"}</p>
        </div>
        <div className="rounded-2xl glass-card p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Tendance</p>
          <p className={`flex items-center gap-1 font-semibold mt-2 ${tr.c}`}><TrIcon size={18} /> {tr.t}</p>
        </div>
      </div>

      {hist.length > 0 && (
        <div className="rounded-2xl glass-card p-4 mb-4">
          <p className="text-xs uppercase tracking-widest text-pink-400 font-bold mb-3">Évolution du health score</p>
          <div className="flex items-end gap-1.5 h-24">
            {hist.slice(-20).map((h, i) => (
              <div key={i} className="flex-1 h-full flex items-end" title={`${h.score} · ${fmtDate(h.at)}`}>
                <div className="w-full rounded-t transition-all" style={{ height: `${Math.max(4, Math.min(100, h.score))}%`, background: healthColor(h.score) }} />
              </div>
            ))}
          </div>
        </div>
      )}

      <AttachmentHint data={data} rel={rel} user={user} id={id} navigate={navigate} />

      <button
        data-testid="rel-profile-cta"
        onClick={() => { vibrate(); navigate(`/profile/${id}`); }}
        className="w-full flex items-center gap-3 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/25 hover:border-purple-500/50 transition-colors p-4 mb-4 text-left"
      >
        <div className="w-11 h-11 rounded-xl bg-purple-500/15 flex items-center justify-center text-purple-300 shrink-0">
          <Brain size={20} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold flex items-center gap-2">
            Profil psychologique
            {rel.profile_needs_update && (
              <span data-testid="rel-profile-update-badge" className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-purple-300 bg-purple-500/15 border border-purple-500/25 rounded-full px-2 py-0.5">
                <TrendingUp size={10} /> À mettre à jour
              </span>
            )}
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">Radar de personnalité, tendances (pervers narcissique, love bombing…) et signaux — sur {rel.contact_name} ou toi.</p>
        </div>
        <ChevronRight size={18} className="text-zinc-500 shrink-0" />
      </button>

      {(data.attachment_style || data.compatibility) ? (
        <div className="grid grid-cols-1 gap-3 mb-4" data-testid="rel-axes">
          {isWhatsapp && msgCount < DEEP_FULL && (
            <p className="text-[11px] text-amber-300/90 flex items-center gap-1.5" data-testid="rel-axes-preview">
              <Sparkles size={12} className="shrink-0" /> Aperçu — le bilan s'affine automatiquement dès {DEEP_FULL} messages captés ({msgCount}/{DEEP_FULL}).
            </p>
          )}
          {data.attachment_style && (
            <div data-testid="rel-attachment" className="rounded-2xl glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Anchor size={15} className="text-cyan-400" />
                <p className="text-xs uppercase tracking-widest text-cyan-400 font-bold">Style d'attachement</p>
              </div>
              <div className="flex gap-6">
                {data.attachment_style.them?.style && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-600">L'autre</p>
                    <p className="font-display text-lg font-black capitalize text-pink-400">{data.attachment_style.them.style}</p>
                  </div>
                )}
                {(QUIZ_FR[user?.attachment_quiz?.style] || data.attachment_style.me?.style) && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-600">Toi {QUIZ_FR[user?.attachment_quiz?.style] && <span className="text-purple-400">· test</span>}</p>
                    <p className="font-display text-lg font-black capitalize text-purple-400">{QUIZ_FR[user?.attachment_quiz?.style] || data.attachment_style.me.style}</p>
                  </div>
                )}
              </div>
              {data.attachment_style.verdict && <p className="text-xs text-zinc-400 mt-2">{data.attachment_style.verdict}</p>}
            </div>
          )}
          {data.compatibility && (
            <div data-testid="rel-compatibility" className="rounded-2xl glass-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <HeartHandshake size={15} className="text-pink-400" />
                  <p className="text-xs uppercase tracking-widest text-pink-400 font-bold">Compatibilité</p>
                </div>
                {typeof data.compatibility.score === "number" && (
                  <span className="font-display text-2xl font-black" style={{ color: healthColor(data.compatibility.score) }}>
                    {data.compatibility.score}
                  </span>
                )}
              </div>
              {data.compatibility.verdict && <p className="text-xs text-zinc-400">{data.compatibility.verdict}</p>}
              {(data.compatibility.dimensions || []).length > 0 && (
                <div className="mt-3 space-y-2">
                  {data.compatibility.dimensions.slice(0, 3).map((b, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-[11px] mb-0.5">
                        <span className="text-zinc-400">{b.label}</span>
                        <span className="text-zinc-500">{b.score}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-pink-500 to-purple-500" style={{ width: `${b.score}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <button
          data-testid="rel-axes-cta"
          onClick={() => (isWhatsapp ? syncNow() : navigate("/import"))}
          disabled={isWhatsapp && syncing}
          className="w-full mb-4 rounded-2xl border border-dashed border-zinc-700 hover:border-pink-500 transition-colors p-4 flex items-center gap-3 text-left disabled:opacity-60"
        >
          <div className="flex -space-x-1">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center text-cyan-400"><Anchor size={15} /></div>
            <div className="w-8 h-8 rounded-lg bg-pink-500/15 flex items-center justify-center text-pink-400"><HeartHandshake size={15} /></div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold flex items-center gap-1"><Sparkles size={13} className="text-pink-400" /> Style d'attachement & compatibilité</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {isWhatsapp
                ? (msgCount >= DEEP_MIN
                    ? `Assez de messages captés (${msgCount}) — clique pour lancer le bilan complet.`
                    : `Bilan disponible dès ${DEEP_MIN} messages captés (${msgCount}/${DEEP_MIN}). Synchronise ou attends de nouveaux échanges.`)
                : `Lance une analyse « Attachement » ou « Compatibilité » sur ${rel.contact_name}.`}
            </p>
          </div>
        </button>
      )}

      {isWhatsapp ? (
        <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-pink-500/10 border border-emerald-500/25 p-4 mb-5" data-testid="rel-wa-sync">
          <div className="flex items-start gap-3">
            <MessageCircle className="text-emerald-400 shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">Suivi WhatsApp actif</p>
              <p className="text-xs text-zinc-400 mt-1">
                {msgCount > 0
                  ? `${msgCount} message${msgCount > 1 ? "s" : ""} capturé${msgCount > 1 ? "s" : ""}. Synchronise pour récupérer l'historique récent (3-7 j) et lancer l'analyse.`
                  : "Aucun message encore. Synchronise l'historique récent ou attends de nouveaux échanges — ils sont captés automatiquement."}
              </p>
            </div>
          </div>
          {msgCount > 0 && (() => {
            const lvl = msgCount >= 150
              ? { label: "Analyse complète", pct: 100, color: "#10b981" }
              : msgCount >= 30
                ? { label: "Bonne base", pct: 62, color: "#eab308" }
                : { label: "Aperçu rapide", pct: 26, color: "#f59e0b" };
            return (
              <div className="mt-3" data-testid="rel-wa-confidence">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-zinc-500">Fiabilité du bilan</span>
                  <span className="text-[11px] font-bold" style={{ color: lvl.color }}>{lvl.label}</span>
                </div>
                <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${lvl.pct}%`, background: lvl.color }} />
                </div>
                {msgCount < 30 && (
                  <p className="text-[11px] text-amber-300/90 mt-2">
                    Peu de matière pour l'instant — importe l'historique .txt pour un bilan complet.
                  </p>
                )}
              </div>
            );
          })()}
          <div className="flex gap-2 mt-3">
            <button data-testid="rel-wa-sync-btn" onClick={syncNow} disabled={syncing}
              className="flex-1 flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white text-sm font-bold py-2.5 hover:bg-pink-600 disabled:opacity-60 transition-colors">
              {syncing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              {syncing ? "Synchronisation…" : "Synchroniser & analyser"}
            </button>
          </div>
          <button data-testid="rel-wa-import-file" onClick={() => navigate("/import")}
            className="w-full text-xs text-zinc-500 hover:text-zinc-300 mt-2 py-1">
            Historique complet ? Importer le fichier .txt
          </button>
        </div>
      ) : (
        <div className="rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/20 p-4 mb-5 flex items-start gap-3">
          <Bell className="text-pink-400 shrink-0 mt-0.5" size={18} />
          <div>
            <p className="text-sm font-semibold">Suivi continu & alertes push <span className="text-[9px] font-bold tracking-wider text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full align-middle">BIENTÔT</span></p>
            <p className="text-xs text-zinc-400 mt-1">Connecte WhatsApp pour un suivi automatique : le Doc t'alertera dès qu'une dégradation ou un red flag apparaît.</p>
          </div>
        </div>
      )}

      {(data.alerts || []).length > 0 && (
        <div className="mb-5">
          <h2 className="text-xs uppercase tracking-widest text-red-400 font-bold mb-2">Alertes ({data.alerts.length})</h2>
          <div className="space-y-2" data-testid="rel-alerts">
            {data.alerts.map((a) => (
              <div key={a.alert_id} className="rounded-xl bg-red-500/10 border border-red-500/30 p-3">
                <p className="text-sm font-semibold text-red-300">🚩 {a.title}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{a.message}</p>
                <p className="text-[10px] text-zinc-600 mt-1">{fmtDate(a.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">Timeline des analyses</h2>
      <div className="space-y-2" data-testid="rel-timeline">
        {data.analyses.map((a) => {
          const meta = VLABEL[a.vertical] || VLABEL.red_flags;
          const Icon = meta.icon;
          const badge = a.grade || (typeof a.score === "number" ? a.score : null);
          return (
            <button key={a.analysis_id} onClick={() => navigate(`/analysis/${a.analysis_id}`)}
              className="w-full flex items-center gap-3 rounded-2xl glass-card p-3 text-left hover:border-zinc-700 transition-colors">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center text-pink-400 shrink-0"><Icon size={16} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">{meta.label}</p>
                <p className="text-xs text-zinc-500">{fmtDate(a.created_at)}</p>
              </div>
              {badge !== null && <span className="font-display text-lg font-black text-pink-400">{badge}</span>}
            </button>
          );
        })}
      </div>
    </Layout>
  );
}


function AttachmentHint({ data, rel, user, id, navigate }) {
  const themFr = normStyle(data.attachment_style?.them?.style);
  if (!themFr) return null; // no attachment analysis yet → nothing to hint
  const meFr = QUIZ_FR[user?.attachment_quiz?.style] || null;
  const contact = (rel.contact_name || "l'autre").split(" ")[0];

  if (meFr) {
    return (
      <div data-testid="rel-attachment-hint" className="rounded-2xl bg-gradient-to-br from-purple-500/12 to-pink-500/12 border border-purple-500/25 p-4 mb-4">
        <p className="text-[10px] uppercase tracking-widest text-purple-300 font-bold mb-2">🧠 Ton style × son profil</p>
        <div className="flex items-center gap-2 text-sm font-bold">
          <span>{STYLE_EMO[meFr]} {cap(meFr)}</span>
          <span className="text-zinc-500">↔</span>
          <span>{STYLE_EMO[themFr]} {cap(themFr)}</span>
        </div>
        <p className="text-sm text-zinc-300 mt-2">{trapText(meFr, themFr)}</p>
        <button data-testid="rel-hint-cta" onClick={() => { vibrate(); navigate(`/profile/${id}`); }}
          className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-pink-300 hover:text-pink-200">
          Voir le Verdict complet <ChevronRight size={15} />
        </button>
      </div>
    );
  }

  // No quiz done yet → funnel the user into the attachment quiz.
  return (
    <div data-testid="rel-attachment-hint-nudge" className="rounded-2xl bg-gradient-to-br from-purple-500/12 to-pink-500/12 border border-purple-500/25 p-4 mb-4">
      <p className="text-sm font-bold">🧠 Débloque VOTRE dynamique de couple</p>
      <p className="text-xs text-zinc-400 mt-0.5">
        {cap(contact)} semble plutôt <span className="text-pink-300 font-semibold">{STYLE_EMO[themFr]} {themFr}</span>.
        Fais le test d'attachement (2 min) pour voir comment vos styles s'accordent.
      </p>
      <button data-testid="rel-hint-quiz-cta" onClick={() => { vibrate(); navigate("/quiz"); }}
        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-pink-500 hover:bg-pink-600 text-white text-xs font-bold px-4 py-2">
        Faire le test <ChevronRight size={14} />
      </button>
    </div>
  );
}
